// Academias y roster (Scout QA — membresía de academia).
// Reemplaza src/utils/academy.js, que leía las tablas de Supabase directamente.
//
// Regla de negocio: si un atleta está CONFIRMADO en la academia verificada del
// scout, el scout ve su perfil completo SIN solicitud de contacto. Por eso la
// membresía necesita a las dos partes (DEV-51): el atleta pide unirse y un coach
// la confirma, o la academia lo invita y el ATLETA acepta.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";

// Directorio de academias, para sugerir en el registro y evitar duplicados por
// typos ("Internacional FC" vs "internacional fc").
export const useAcademies = (options = {}) =>
  useQuery({
    queryKey: ["academies"],
    queryFn: () => apiFetch("/academies", { query: { sort: "name", order: "asc", pageSize: 100 } }),
    select: (data) => data?.items || [],
    ...options,
  });

/**
 * Resuelve una academia por nombre, creándola si no existe.
 *
 * El backend hace el find-or-create case-insensitive (hay un índice único sobre
 * lower(name)), así que llamar dos veces con el mismo nombre devuelve la misma
 * fila en vez de fallar.
 */
export const useFindOrCreateAcademy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, country, city }) =>
      apiFetch("/academies", {
        method: "POST",
        body: { name: (name || "").trim(), country: country || null, city: city || null },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["academies"] }),
  });
};

/**
 * Set de athlete_ids (como String) confirmados en la academia del scout
 * autenticado — el gate de "perfil completo sin solicitud".
 *
 * Devuelve Strings porque los ids llegan como number desde la API pero se
 * comparan contra claves que a veces son string; normalizar acá evita que el
 * gate falle silenciosamente por tipo.
 *
 * El backend resuelve la academia desde la sesión, así que no lleva parámetros:
 * un scout no puede consultar el roster de otra academia.
 */
export const useAcademyRoster = (options = {}) =>
  useQuery({
    queryKey: ["academy-athletes", "my-roster"],
    queryFn: () => apiFetch("/academy-athletes/my-roster"),
    select: (data) => new Set((data?.athlete_ids || []).map(String)),
    // El roster lo cura la academia en otra sesión: revalidar al volver a la
    // pestaña mantiene el gate fresco sin polling.
    refetchOnWindowFocus: true,
    ...options,
  });

// ---- Membresía del atleta ----------------------------------------------------
// El atleta pide unirse a una academia y un coach verificado la confirma, o la
// academia lo invita y él acepta o rechaza. Una sola membresía viva por atleta
// (pending o confirmed); `requested_by` dice quién la inició.

/** { membership: { id, academy_id, academy_name, status, requested_by, ... } | null } */
export const useMyAcademyMembership = (options = {}) =>
  useQuery({
    queryKey: ["academy-athletes", "me"],
    queryFn: () => apiFetch("/academy-athletes/me"),
    select: (data) => data?.membership ?? null,
    ...options,
  });

const useMembershipMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      queryClient.setQueryData(["academy-athletes", "me"], data);
      queryClient.invalidateQueries({ queryKey: ["academy-athletes"] });
    },
  });
};

export const useJoinAcademy = () =>
  useMembershipMutation((academyId) =>
    apiFetch("/academy-athletes/join", { method: "POST", body: { academy_id: academyId } }),
  );

/** Cancela una solicitud pendiente o sale de la academia. */
export const useLeaveAcademy = () =>
  useMembershipMutation(() => apiFetch("/academy-athletes/leave", { method: "POST" }));

/** Invitaciones pendientes de academias: [{ id, academy_id, academy_name, created_at }]. */
export const useAcademyInvitations = (options = {}) =>
  useQuery({
    queryKey: ["academy-athletes", "invitations"],
    queryFn: () => apiFetch("/academy-athletes/invitations"),
    select: (data) => data?.items || [],
    ...options,
  });

/** El atleta acepta la invitación de una academia (id de la fila academy_athletes). */
export const useAcceptAcademyInvitation = () =>
  useMembershipMutation((id) => apiFetch(`/academy-athletes/${id}/accept`, { method: "POST" }));

/** El atleta rechaza la invitación de una academia. */
export const useDeclineAcademyInvitation = () =>
  useMembershipMutation((id) => apiFetch(`/academy-athletes/${id}/decline`, { method: "POST" }));

// ---- Roster del coach --------------------------------------------------------

/** { academy: { id, name, verified } | null, items: [...] } — pendientes primero. */
export const useAcademyRosterMembers = (options = {}) =>
  useQuery({
    queryKey: ["academy-athletes", "roster"],
    queryFn: () => apiFetch("/academy-athletes/roster"),
    refetchOnWindowFocus: true,
    ...options,
  });

/**
 * decision: "confirm" | "remove". "confirm" solo vale para solicitudes del
 * atleta; una invitación de la academia la acepta el atleta (409
 * athlete_must_accept si el coach lo intenta).
 */
export const useDecideRosterMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }) =>
      apiFetch(`/academy-athletes/${id}/${decision}`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academy-athletes"] });
      queryClient.invalidateQueries({ queryKey: ["my-athletes"] });
      // Confirming un-redacts the athlete's contact details for this coach.
      queryClient.invalidateQueries({ queryKey: ["athletes"] });
    },
  });
};

/**
 * "Mis deportistas" del scout: roster confirmado de su academia verificada +
 * atletas que aprobaron una de sus solicitudes de contacto. Cada fila trae
 * in_academy / has_contact / contact_request_id.
 */
export const useMyAthletes = (options = {}) =>
  useQuery({
    queryKey: ["my-athletes"],
    queryFn: () => apiFetch("/scouting/my-athletes"),
    select: (data) => data?.items || [],
    ...options,
  });
